import { createContext, useContext } from 'react';
import type { WidgetKey, WidgetScreen } from '@/domain';
import type { ReportRange } from '@/application/ports/ReportRepository';

/**
 * What a widget may ask of the screen it is drawn on. The lab's request of
 * 12 October 2026.
 *
 * Which screen it is, so a widget that lists the person's screens leaves this
 * one out. Whether the screen reads top to bottom, as the Overview does, or
 * lets widgets stand side by side. The reporting range chosen at its top, for
 * the widgets that count over one. And the way to take a widget off the screen
 * from the widget itself, which the notifications carry beside their settings.
 */
export interface WidgetBoardState {
  screen: WidgetScreen;
  stacked: boolean;
  range: ReportRange;
  /** Takes the widget off this screen, kept on the person's account, and offers to put it back. */
  hide: (key: WidgetKey) => void;
}

const WidgetBoardContext = createContext<WidgetBoardState | null>(null);

export const WidgetBoardProvider = WidgetBoardContext.Provider;

/** The screen a widget is on. Null where it is drawn outside one, a test of the widget alone among them. */
export function useWidgetBoard(): WidgetBoardState | null {
  return useContext(WidgetBoardContext);
}

/** The reporting range a widget counts over: the screen's, or today where there is no screen. */
export function useWidgetRange(): ReportRange {
  return useContext(WidgetBoardContext)?.range ?? 'today';
}
